import { Rng, type MapId, type Role } from '@il-colpo/engine';
import { CHALLENGES } from './balance.ts';
import { dayKey, weekKey } from './calendar.ts';
import type { MatchPlayer, MatchRecord } from './types.ts';

/**
 * Daily and weekly challenges (GDD §15). The catalogue is code and the choice of what is on offer is
 * a pure function of the period (`2026-10-21`, `2026-W43`), so the server, the client and the jobs
 * agree without a table of definitions. A challenge's words are in the translation files, by metric
 * (with `goal`, a role or a map as parameters). Rewards are XP, paid when the goal is reached.
 */

export type ChallengePeriod = 'daily' | 'weekly';

export type ChallengeMetric =
  | 'matches'
  | 'heists'
  | 'wins'
  | 'loot'
  | 'unmasksRight'
  | 'infiltratorWins'
  | 'cleanHeists'
  | 'dailyRuns'
  | `role:${Role}`
  | `map:${MapId}`;

export interface ChallengeDef {
  id: string;
  period: ChallengePeriod;
  metric: ChallengeMetric;
  goal: number;
  rewardXp: number;
}

const daily = (
  id: string,
  metric: ChallengeMetric,
  goal: number,
  rewardXp: number,
): ChallengeDef => ({
  id,
  period: 'daily',
  metric,
  goal,
  rewardXp,
});
const weekly = (
  id: string,
  metric: ChallengeMetric,
  goal: number,
  rewardXp: number,
): ChallengeDef => ({
  id,
  period: 'weekly',
  metric,
  goal,
  rewardXp,
});

export const CHALLENGE_CATALOG: readonly ChallengeDef[] = [
  daily('d_play_2', 'matches', 2, 60),
  daily('d_heist_1', 'heists', 1, 80),
  daily('d_win_1', 'wins', 1, 80),
  daily('d_clean_1', 'cleanHeists', 1, 120),
  daily('d_unmask_1', 'unmasksRight', 1, 100),
  daily('d_locksmith', 'role:locksmith', 1, 70),
  daily('d_hacker', 'role:hacker', 1, 70),
  daily('d_mastermind', 'role:mastermind', 1, 70),
  daily('d_lookout', 'role:lookout', 1, 70),
  daily('d_driver', 'role:driver', 1, 70),
  daily('d_loot_80', 'loot', 80, 90),
  weekly('w_play_8', 'matches', 8, 250),
  weekly('w_heist_5', 'heists', 5, 300),
  weekly('w_win_4', 'wins', 4, 300),
  weekly('w_clean_2', 'cleanHeists', 2, 350),
  weekly('w_unmask_3', 'unmasksRight', 3, 350),
  weekly('w_infiltrator_1', 'infiltratorWins', 1, 400),
  weekly('w_daily_3', 'dailyRuns', 3, 300),
  weekly('w_loot_400', 'loot', 400, 350),
  weekly('w_bank', 'map:bank', 3, 220),
  weekly('w_museum', 'map:museum', 3, 220),
  weekly('w_villa', 'map:villa', 3, 220),
  weekly('w_express', 'map:express', 3, 220),
];

/** The challenge of a catalogue id, if there is one. */
export function challengeById(id: string): ChallengeDef | undefined {
  return CHALLENGE_CATALOG.find((c) => c.id === id);
}

/** The key under which a player's progress on one challenge of one period is kept. */
export function challengeKey(period: ChallengePeriod, periodKey: string, id: string): string {
  return `${period}:${periodKey}:${id}`;
}

/** The period key (the day or the week) a moment belongs to. */
export function periodKeyOf(period: ChallengePeriod, ms: number): string {
  return period === 'daily' ? dayKey(ms) : weekKey(ms);
}

/**
 * What is on offer in a period: the same for everybody, chosen by the period's key, never two on the
 * same metric.
 */
export function challengesFor(period: ChallengePeriod, periodKey: string): ChallengeDef[] {
  const count = period === 'daily' ? CHALLENGES.dailyCount : CHALLENGES.weeklyCount;
  const rng = Rng.create(`challenges/${period}/${periodKey}`);
  const pool = rng.shuffle(CHALLENGE_CATALOG.filter((c) => c.period === period));
  const chosen: ChallengeDef[] = [];
  for (const candidate of pool) {
    if (chosen.length === count) break;
    if (chosen.some((c) => c.metric === candidate.metric)) continue;
    chosen.push(candidate);
  }
  return chosen;
}

/** All the challenges on offer at a moment, daily first. */
export function activeChallenges(
  ms: number,
): { def: ChallengeDef; key: string; periodKey: string }[] {
  const out: { def: ChallengeDef; key: string; periodKey: string }[] = [];
  for (const period of ['daily', 'weekly'] as const) {
    const periodKey = periodKeyOf(period, ms);
    for (const def of challengesFor(period, periodKey)) {
      out.push({ def, key: challengeKey(period, periodKey, def.id), periodKey });
    }
  }
  return out;
}

/** How far one match takes a player towards a metric. Somebody who left makes no progress. */
export function metricGain(
  metric: ChallengeMetric,
  match: Pick<MatchRecord, 'success' | 'alarmFinal' | 'mapId' | 'mode'>,
  player: MatchPlayer,
): number {
  if (player.abandoned) return 0;
  switch (metric) {
    case 'matches':
      return 1;
    case 'heists':
      return match.success ? 1 : 0;
    case 'wins':
      return player.won ? 1 : 0;
    case 'loot':
      return player.earned;
    case 'unmasksRight':
      return player.accused === 'right' ? 1 : 0;
    case 'infiltratorWins':
      return player.infiltrator && player.won ? 1 : 0;
    case 'cleanHeists':
      // A success that kept the alarm at 30 % or less.
      return match.success && match.alarmFinal <= 300 ? 1 : 0;
    case 'dailyRuns':
      return match.mode === 'daily' ? 1 : 0;
    default: {
      const [kind, value] = metric.split(':');
      if (kind === 'role') return player.role === value ? 1 : 0;
      if (kind === 'map') return match.mapId === value ? 1 : 0;
      return 0;
    }
  }
}
